import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const protocols = [
  { name: 'Aave',      icon: '/protocols/aave-brand.png',          url: 'https://aave.com/' },
  { name: 'Compound',  icon: '/protocols/compound-icon.svg',       url: 'https://compound.finance/' },
  { name: 'Uniswap',   icon: '/protocols/uniswap-real.svg',        url: 'https://uniswap.org/' },
  { name: 'Aerodrome', icon: '/protocols/aerodrome-icon-fixed.svg', url: 'https://aerodrome.finance/' },
];

export default function IntegratedProtocols() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });

  return (
    <section ref={sectionRef} data-testid="integrated-protocols-section" className="relative py-10 sm:py-12">
      {/* Top divider */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[50%] h-px bg-gradient-to-r from-transparent via-rax-border/30 to-transparent" />

      <div className="mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center"
        >
          {/* Section label */}
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-6 bg-gradient-to-r from-transparent to-rax-border/30" />
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-rax-body/40">
              Integrated Protocols
            </span>
            <div className="h-px w-6 bg-gradient-to-l from-transparent to-rax-border/30" />
          </div>

          {/* Protocol row — each item: 24×24 icon + text-sm label, uniform gap-10 between groups */}
          <div
            className="flex flex-wrap items-center justify-center gap-10"
            data-testid="protocols-logos"
          >
            {protocols.map((protocol, i) => (
              <motion.a
                key={protocol.name}
                href={protocol.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 8 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.08 + i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group flex items-center gap-2"
                data-testid={`protocol-${protocol.name.toLowerCase()}`}
              >
                {/* Icon — fixed 24×24, grayscale+fade by default */}
                <img
                  src={protocol.icon}
                  alt=""
                  className="w-6 h-6 object-contain filter grayscale opacity-50 transition-all duration-300 ease-out group-hover:grayscale-0 group-hover:opacity-100"
                />
                {/* Label — text-sm, same fade treatment */}
                <span className="text-sm font-medium leading-none text-rax-body/40 transition-colors duration-300 ease-out group-hover:text-rax-body/90 whitespace-nowrap">
                  {protocol.name}
                </span>
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[50%] h-px bg-gradient-to-r from-transparent via-rax-border/30 to-transparent" />
    </section>
  );
}
